<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth.js';
import { useUiStore } from '@/stores/ui.js';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const ui = useUiStore();

const groups = computed(() => [
  {
    label: 'Genel',
    items: [{ to: '/', name: 'dashboard', icon: 'home', label: t('nav.dashboard') }],
  },
  {
    label: t('nav.content'),
    items: [
      { to: '/hotels', name: 'hotels.list', icon: 'hotel', label: t('nav.hotels') },
      { to: '/villas', name: 'villas.list', icon: 'villa', label: t('nav.villas') },
      { to: '/locations', name: 'locations.list', icon: 'location', label: t('nav.locations') },
      { to: '/promotions', name: 'promotions.list', icon: 'spark', label: t('nav.promotions') },
      { to: '/services', name: 'services.list', icon: 'concierge', label: t('nav.services') },
      { to: '/blog', name: 'blog.list', icon: 'doc', label: t('nav.blog') },
      { to: '/testimonials', name: 'testimonials.list', icon: 'quote', label: t('nav.testimonials') },
      { to: '/media', name: 'media.list', icon: 'image', label: t('nav.media') },
    ],
  },
  {
    label: 'İşlemler',
    items: [
      { to: '/booking-requests', name: 'booking-requests.list', icon: 'calendar', label: 'Rezervasyonlar' },
      { to: '/newsletter', name: 'newsletter.list', icon: 'mail', label: t('nav.newsletter') },
      { to: '/contact', name: 'contact.list', icon: 'inbox', label: t('nav.contact') },
    ],
  },
  {
    label: t('nav.system'),
    items: [
      { to: '/settings', name: 'settings', icon: 'gear', label: t('nav.settings') },
      ...(auth.isAdmin
        ? [{ to: '/users', name: 'users.list', icon: 'users', label: t('nav.users') }]
        : []),
    ],
  },
]);

function isActive(item) {
  if (item.to === '/') return route.path === '/';
  return route.path === item.to || route.path.startsWith(item.to + '/');
}

// 16×16 ince konturlu ikonlar (stroke 1.4) — tasarım ikon dili
const icons = {
  home: 'M3 7.5 8 3l5 4.5V13H9.5V9.5h-3V13H3V7.5Z',
  hotel: 'M3.5 13.5V3h6v10.5m3 0V6.5h-3M5.5 5.5h2M5.5 8h2M5.5 10.5h2M2 13.5h12',
  villa: 'M2.5 13h11M3.5 13V7L8 3.5 12.5 7v6M6.5 13V9.5h3V13',
  location: 'M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0C3.5 9.8 8 14 8 14Zm0-6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  spark: 'M8 2l1.2 3.8L13 7l-3.8 1.2L8 12l-1.2-3.8L3 7l3.8-1.2L8 2Z',
  concierge: 'M2.5 11.5h11M3.5 11.5a4.5 4.5 0 0 1 9 0M8 7V5.5M6.5 13.5h3',
  doc: 'M4 2.5h5.5L12 5v8.5H4Zm5.5 0V5H12M6 7.5h4M6 10h4',
  quote: 'M3 4.5h4.5v4.5H4.8A2.8 2.8 0 0 0 7.5 11.5v1A4.5 4.5 0 0 1 3 8V4.5Zm6 0h4.5v4.5h-2.7a2.8 2.8 0 0 0 2.7 2.5v1A4.5 4.5 0 0 1 9 8V4.5Z',
  image: 'M2.5 3.5h11v9h-11Zm0 6.5 3-3 3 3 2.5-2.5 2.5 2.5',
  calendar: 'M3 4.5h10v9H3Zm0 3h10M5.5 3v2.5M10.5 3v2.5',
  mail: 'M2.5 4.5h11v7h-11Zm0 .5L8 9.5 13.5 5',
  inbox: 'M2.5 9.5h3.2L7 11.5h2l1.3-2h3.2M2.5 9.5V4a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v5.5m-11 0V12a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9.5',
  gear: 'M8 5.5A2.5 2.5 0 1 0 8 10.5 2.5 2.5 0 0 0 8 5.5Zm-5.5 2.5h1.2M12.3 8h1.2M8 2.5v1.2M8 12.3v1.2M4.1 4.1l.9.9M11 11l.9.9M11.9 4.1l-.9.9M5 11l-.9.9',
  users: 'M6 7.5a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Zm-3.5 5.5c0-2 1.6-3.2 3.5-3.2s3.5 1.2 3.5 3.2M10.5 7a2 2 0 1 0-.9-3.8M10.8 9.9c1.6.2 2.7 1.3 2.7 3.1',
};

const initials = computed(() => {
  const n = auth.user?.name || auth.user?.email || '?';
  const parts = n.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
});

async function doLogout() {
  await auth.logout();
  router.push('/login');
}
</script>

<template>
  <aside
    class="fixed lg:static inset-y-0 left-0 z-40 w-[228px] flex flex-col transition-transform"
    :class="ui.sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'"
    style="background: #09205C;"
  >
    <div class="px-[22px] pt-6 pb-5">
      <RouterLink to="/" class="block" @click="ui.closeSidebar">
        <p class="font-display text-[23px] font-semibold leading-none text-white lowercase">vip travellers</p>
        <p class="text-[9.5px] uppercase tracking-[0.2em] font-semibold text-(--admin-gold) mt-[7px]">Yönetim Paneli</p>
      </RouterLink>
    </div>

    <nav class="flex-1 overflow-y-auto pb-3">
      <div v-for="group in groups" :key="group.label">
        <p class="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5163A0] px-[22px] pt-3.5 pb-1.5">{{ group.label }}</p>
        <ul>
          <li v-for="item in group.items" :key="item.to">
            <RouterLink
              :to="item.to"
              @click="ui.closeSidebar"
              class="flex items-center gap-[11px] w-full h-[38px] pl-5 pr-3.5 text-[13.5px] font-medium border-l-2 transition-colors"
              :class="
                isActive(item)
                  ? 'bg-white/[0.08] text-white border-(--admin-gold)'
                  : 'text-[#8FA0C8] border-transparent hover:text-white'
              "
            >
              <svg viewBox="0 0 16 16" class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path :d="icons[item.icon]" /></svg>
              <span class="flex-1 truncate">{{ item.label }}</span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </nav>

    <div class="border-t border-white/10 px-5 py-3.5 flex items-center gap-2.5">
      <div class="w-[30px] h-[30px] rounded-full bg-[#1B2B72] text-(--admin-gold) grid place-items-center text-[11px] font-bold shrink-0">
        {{ initials }}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-white text-[12.5px] font-semibold truncate">{{ auth.user?.name || auth.user?.email }}</p>
        <p class="text-[#8FA0C8] text-[11px]">{{ auth.user?.role === 'admin' ? 'Yönetici' : 'İçerik Editörü' }}</p>
      </div>
      <button
        @click="doLogout"
        title="Çıkış"
        class="w-8 h-8 rounded-lg grid place-items-center text-[#8FA0C8] hover:text-white hover:bg-white/[0.08] transition-colors shrink-0"
      >
        <svg viewBox="0 0 16 16" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 3H3v10h3.5M10 5.5 13 8l-3 2.5M6 8h7" /></svg>
      </button>
    </div>
  </aside>

  <!-- Mobile overlay -->
  <div
    v-if="ui.sidebarOpen"
    @click="ui.closeSidebar"
    class="lg:hidden fixed inset-0 z-30 bg-black/40"
  />
</template>
